import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import TickerBar from "./TickerBar";
import { api, clearAuth, getAuth } from "../api";
import { buildItemUrl } from "../utils/itemUrl";
import ImageWithFallback from "./ImageWithFallback";
import { usePriceVisibility } from "../contexts/PriceVisibilityContext";

const MAX_SCAN_IMAGE_BYTES = 6 * 1024 * 1024;
const LOW_CONFIDENCE_THRESHOLD = 0.55;
const DEBUG_SCAN = /^(1|true|yes|on)$/i.test(String(process.env.REACT_APP_DEBUG_SCAN || process.env.DEBUG_SCAN || ""));
const scanDebugLog = (...args) => {
  if (DEBUG_SCAN) console.log(...args);
};
const directOpenItemId = (payload = {}) => Number(payload?.final_item_id || payload?.matched_item_id || payload?.item_id || 0);
const readExifOrientation = async (file) => {
  try {
    const buf = await file.arrayBuffer();
    const view = new DataView(buf);
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return 1;

    let offset = 2;
    while (offset + 4 < view.byteLength) {
      const marker = view.getUint16(offset, false);
      offset += 2;
      if (marker === 0xffda || marker === 0xffd9) break;
      if ((marker & 0xff00) !== 0xff00) break;

      const size = view.getUint16(offset, false);
      if (size < 2 || offset + size > view.byteLength) break;

      if (marker === 0xffe1) {
        const tiffOffset = offset + 2;
        if (tiffOffset + 8 > view.byteLength) break;
        if (view.getUint32(tiffOffset, false) !== 0x45786966) break;

        const endianOffset = tiffOffset + 6;
        const little = view.getUint16(endianOffset, false) === 0x4949;
        const firstIfdOffset = view.getUint32(endianOffset + 4, little);
        let ifdOffset = endianOffset + firstIfdOffset;
        if (ifdOffset + 2 > view.byteLength) break;

        const entries = view.getUint16(ifdOffset, little);
        ifdOffset += 2;

        for (let i = 0; i < entries; i += 1) {
          const entryOffset = ifdOffset + i * 12;
          if (entryOffset + 12 > view.byteLength) break;
          const tag = view.getUint16(entryOffset, little);
          if (tag === 0x0112) {
            const orientation = view.getUint16(entryOffset + 8, little);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }
      }

      offset += size;
    }
  } catch {
    // fall through
  }
  return 1;
};

const loadImageForScan = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bitmap, orientationApplied: true };
    } catch {
      // fallback below
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = objectUrl;
    });
    return { source: img, orientationApplied: false };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const compressForScan = async (file) => {
  const { source, orientationApplied } = await loadImageForScan(file);
  const orientation = orientationApplied ? 1 : await readExifOrientation(file);

  const srcW = Math.max(1, Number(source.width || source.naturalWidth || 1));
  const srcH = Math.max(1, Number(source.height || source.naturalHeight || 1));
  const maxDim = 1024;
  const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
  const drawW = Math.max(1, Math.round(srcW * scale));
  const drawH = Math.max(1, Math.round(srcH * scale));

  const rotated = orientation >= 5 && orientation <= 8;
  const canvas = document.createElement("canvas");
  canvas.width = rotated ? drawH : drawW;
  canvas.height = rotated ? drawW : drawH;

  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("canvas_context_unavailable");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  switch (orientation) {
    case 2:
      ctx.translate(drawW, 0);
      ctx.scale(-1, 1);
      break;
    case 3:
      ctx.translate(drawW, drawH);
      ctx.rotate(Math.PI);
      break;
    case 4:
      ctx.translate(0, drawH);
      ctx.scale(1, -1);
      break;
    case 5:
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    case 6:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(0, -drawH);
      break;
    case 7:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(drawW, -drawH);
      ctx.scale(-1, 1);
      break;
    case 8:
      ctx.rotate(-0.5 * Math.PI);
      ctx.translate(-drawW, 0);
      break;
    default:
      break;
  }

  ctx.drawImage(source, 0, 0, drawW, drawH);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (!b) {
        reject(new Error("scan_compress_failed"));
        return;
      }
      resolve(b);
    }, "image/jpeg", 0.75);
  });

  if (typeof source.close === "function") {
    source.close();
  }

  return blob;
};
const Header = () => {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  const appBarRef = useRef(null);
  const scanInputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanStep, setScanStep] = useState("");
  const [scanError, setScanError] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanCreateFile, setScanCreateFile] = useState(null);
  const [scanLookupMatch, setScanLookupMatch] = useState(null);
  const [scanEditValue, setScanEditValue] = useState("");
  const [legoNumberEdit, setLegoNumberEdit] = useState("");
  const [legoNumberEditing, setLegoNumberEditing] = useState(false);
  const [legoNumberSaving, setLegoNumberSaving] = useState(false);
  const navigate = useNavigate();

  // Breakpoints
  const isUnder425 = useMediaQuery("(max-width:425px)");
  const isLarge = useMediaQuery("(min-width:1200px)");
  const isMobileLayout = !isLarge;

  const DEFAULT_AVATAR = `${process.env.PUBLIC_URL || ""}/logos/default-avatar.jpg`;

  useEffect(() => {
    const updateUserState = () => {
      try {
        const { user } = getAuth();
        const avatar =
          user?.profile_picture_url ||
          user?.profilePictureUrl ||
          user?.profile_picture ||
          user?.avatar_url ||
          user?.image_url ||
          null;
        setProfilePic(avatar);
        setIsLoggedIn(Boolean(user?.id));
        setCurrentUser(user || null);
      } catch {
        setProfilePic(null);
        setIsLoggedIn(false);
        setCurrentUser(null);
      }
    };
    updateUserState();
    window.addEventListener("storage", updateUserState);
    return () => window.removeEventListener("storage", updateUserState);
  }, []);

  useLayoutEffect(() => {
    const setOffset = () => {
      const h = appBarRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty("--tcs-header-offset", `${h}px`);
    };
    setOffset();
    window.addEventListener("resize", setOffset);
    return () => window.removeEventListener("resize", setOffset);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFriendRequestCount = async () => {
      if (!isLoggedIn) {
        setFriendRequestCount(0);
        return;
      }
      try {
        const { data } = await api.get("/api/friends/requests");
        if (!mounted) return;
        const requests = Array.isArray(data?.requests) ? data.requests : [];
        setFriendRequestCount(requests.length);
      } catch (e) {
        if (!mounted) return;
        if (e?.response?.status === 401) {
          setFriendRequestCount(0);
        }
      }
    };
    loadFriendRequestCount();
    return () => { mounted = false; };
  }, [isLoggedIn]);

  const friendRequestBadge = friendRequestCount > 9 ? "9+" : friendRequestCount;
  const showFriendRequestBadge = friendRequestCount > 0;

  const handleMenuOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    clearAuth();
    setProfilePic(null);
    setCurrentUser(null);
    setFriendRequestCount(0);
    navigate("/login");
  };

  const wishlistPath = currentUser?.id
    ? `/wishlist/${encodeURIComponent(currentUser.username || currentUser.name || currentUser.id)}`
    : "/wishlist";

  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    // Use `q` to match backend param and Search page parsing.
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchQuery("");
  };

  const openScanModal = useCallback(() => {
    setScanOpen(true);
    setScanLoading(false);
    setScanStep("");
    setScanError("");
    setScanResult(null);
    setScanCreateFile(null);
    setScanLookupMatch(null);
    setScanEditValue("");
    setLegoNumberEdit("");
    setLegoNumberEditing(false);
  }, []);

  useEffect(() => {
    const handleExternalScanOpen = () => openScanModal();
    window.addEventListener("tcs:open-scan", handleExternalScanOpen);
    return () => window.removeEventListener("tcs:open-scan", handleExternalScanOpen);
  }, [openScanModal]);

  const closeScanModal = () => {
    setScanOpen(false);
    setScanLoading(false);
    setScanStep("");
    setScanError("");
    setScanResult(null);
    setScanCreateFile(null);
    setScanLookupMatch(null);
    setScanEditValue("");
    setLegoNumberEdit("");
    setLegoNumberEditing(false);
    if (scanInputRef.current) scanInputRef.current.value = "";
  };

  const handleChooseScanFile = () => {
    if (scanInputRef.current) scanInputRef.current.click();
  };

  const buildQueryFromIdent = useCallback((scanData) => {
    const ident = scanData?.ident || {};
    const type = String(ident?.type_hint || "").toLowerCase();
    const subject = String(ident?.subject || "").trim();
    const number = String(ident?.number || "").trim();
    const setName = String(ident?.set_name || "").trim();
    const variant = String(ident?.variant || ident?.edition_variant || "").trim();

    let q = "";
    if (type === "sports_card") {
      q = [subject, setName, number].filter(Boolean).join(" ");
    } else if (type === "pokemon" || type === "one_piece") {
      q = [subject, number, setName].filter(Boolean).join(" ");
    } else if (type === "funko") {
      q = [subject, number].filter(Boolean).join(" ");
    } else {
      q = [subject, number, setName].filter(Boolean).join(" ");
    }
    if (variant && !q.toLowerCase().includes(variant.toLowerCase())) {
      q = `${q} ${variant}`.trim();
    }
    if (!q) q = String(scanData?.raw || scanData?.ident?.best_raw || "").trim();
    return q.trim();
  }, []);

  const cleanScanField = useCallback((value) => {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text) return "";
    const lower = text.toLowerCase();
    if (["n/a", "na", "unknown", "none", "null", "item", "collectible"].includes(lower)) return "";
    return text;
  }, []);

  const isUsableEditedName = (value) => {
    const text = cleanScanField(value);
    if (!text) return false;
    if (text.length < 5) return false;
    const normalized = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return false;
    if (["item", "card", "collectible", "game", "toy", "figure", "sports card", "trading card"].includes(normalized)) {
      return false;
    }
    const tokens = normalized.split(" ").filter(Boolean);
    if (tokens.length === 1 && tokens[0].length < 6) return false;
    return true;
  };

  const formatScanCategoryLabel = useCallback((scanData) => {
    const ident = scanData?.ident || {};
    const rawType = cleanScanField(
      ident?.signals?.type_hint ||
      ident?.type_hint ||
      scanData?.item?.type ||
      scanData?.item?.category
    ).toLowerCase();
    const map = {
      one_piece: "One Piece Card",
      one_piece_card: "One Piece Card",
      pokemon: "Pokemon Card",
      pokemon_card: "Pokemon Card",
      tcg: "TCG Card",
      tcg_card: "TCG Card",
      sports_card: "Sports Card",
      video_game: "Video Game",
      game: "Video Game",
      funko: "Funko Pop",
      funko_pop: "Funko Pop",
      lego: "LEGO",
    };
    return map[rawType] || cleanScanField(scanData?.item?.category) || cleanScanField(scanData?.item?.type);
  }, [cleanScanField]);

  const getScanDisplaySummary = useCallback((scanData) => {
    const ident = scanData?.ident || {};
    const signals = ident?.signals || {};
    return {
      title:
        cleanScanField(scanData?.raw) ||
        cleanScanField(ident?.best_raw) ||
        cleanScanField(signals?.name) ||
        cleanScanField(scanData?.item?.name),
      name:
        cleanScanField(signals?.name) ||
        cleanScanField(ident?.subject) ||
        cleanScanField(scanData?.item?.name),
      brand:
        cleanScanField(signals?.brand) ||
        cleanScanField(ident?.brand) ||
        cleanScanField(scanData?.item?.brand),
      category: formatScanCategoryLabel(scanData),
      setName:
        cleanScanField(signals?.set_name) ||
        cleanScanField(ident?.set_name) ||
        cleanScanField(scanData?.item?.set_name),
      number:
        cleanScanField(signals?.code) ||
        cleanScanField(signals?.number) ||
        cleanScanField(ident?.number) ||
        cleanScanField(scanData?.item?.number),
      variant:
        cleanScanField(signals?.variant) ||
        cleanScanField(ident?.variant) ||
        cleanScanField(scanData?.item?.edition_variant),
    };
  }, [cleanScanField, formatScanCategoryLabel]);

  const getCanonicalScanName = useCallback((scanData) => {
    const summary = getScanDisplaySummary(scanData || {});
    return (
      cleanScanField(summary.title) ||
      cleanScanField(summary.name) ||
      cleanScanField(buildQueryFromIdent(scanData || {})) ||
      cleanScanField(scanData?.raw) ||
      cleanScanField(scanData?.ident?.best_raw) ||
      ""
    );
  }, [buildQueryFromIdent, cleanScanField, getScanDisplaySummary]);

const getScanCreateErrorMessage = (payload, fallbackMessage) => {
  if (payload?.error === "image_rejected") {
    return "That photo cannot be processed. Retake it with only the item in frame.";
  }
    const detail = payload?.detail || payload?.error || fallbackMessage;
    if (!detail) return "Could not process scanned item.";
    const text = String(detail).replace(/\s+/g, " ").trim();
    if (text.toLowerCase().includes("invalid_ident_json")) {
      return "Scan data was malformed. Please rescan and try again.";
    }
    if (text.toLowerCase().includes("query_required")) {
      return "The scan did not produce enough item detail. Edit the name or use manual add.";
    }
    if (text.toLowerCase().includes("content_blocked")) {
      return "This item cannot be added because it contains explicit or abusive content.";
  }
  return text;
};
const safeText = (value) => (typeof value === "string" ? value : String(value ?? ""));

  const getScanValidity = (scanData) => {
    const validity = scanData?.ident?.evidence?.collectible_validity || {};
    const confidence = Number(scanData?.confidence ?? scanData?.ident?.confidence ?? 0);
    const summary = getScanDisplaySummary(scanData || {});
    const anchorCount = [summary.name, summary.setName, summary.number, summary.brand, summary.variant].filter(Boolean).length;
    return {
      valid: Boolean(validity?.valid),
      anchorScore: Number(validity?.anchor_score ?? 0),
      reasons: Array.isArray(validity?.reasons) ? validity.reasons : [],
      confidence,
      hasRequiredAnchors: anchorCount >= 2 && Boolean(summary.name || summary.number || summary.setName),
    };
  };

  useEffect(() => {
    if (!scanResult) return;
    setScanEditValue(getCanonicalScanName(scanResult));
  }, [scanResult, getCanonicalScanName]);

    const handleScanFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SCAN_IMAGE_BYTES) {
      setScanError("Image too large, please retake at lower resolution");
      setScanResult(null);
      return;
    }

    setScanLoading(true);
    setScanStep("Uploading photo...");
    setScanError("");
    setScanResult(null);
    setScanLookupMatch(null);

    try {
      let uploadBlob = file;
      let uploadName = file.name || "scan.jpg";
      let uploadType = file.type || "application/octet-stream";

      try {
        const compressedBlob = await compressForScan(file);
        if (compressedBlob?.size) {
          uploadBlob = compressedBlob;
          uploadName = "scan.jpg";
          uploadType = "image/jpeg";
        }
      } catch {
        // Fall back to original file upload.
      }

      const form = new FormData();
      form.append("file", uploadBlob, uploadName);
      if (process.env.NODE_ENV !== "production") {
        scanDebugLog("scan upload", { name: uploadName, size: uploadBlob.size, type: uploadType });
      }

      setScanStep("Identifying item...");
      const { data } = await api.post("/api/scan-photo", form);
      const finalQuery =
        String(data?.query || "").trim() ||
        String(data?.raw || "").trim() ||
        String(data?.ident?.best_raw || "").trim() ||
        String(data?.ident?.subject || "").trim();
      const hasValidScan =
        data?.ok === true &&
        (
          !!String(data?.raw || "").trim() ||
          !!String(data?.ident?.best_raw || "").trim() ||
          !!String(data?.ident?.subject || "").trim()
        );
      scanDebugLog("SCAN RESPONSE", data);
      scanDebugLog("SCAN FINAL QUERY", finalQuery);
      if (!hasValidScan) {
        setScanError(
          safeText(
            data?.message ||
            data?.detail ||
            "Could not identify this item from the photo."
          )
        );
        return;
      }
      const scanData = {
        ...data,
        raw: String(data?.raw || "").trim() || finalQuery,
        query: finalQuery,
      };
      if (scanData?.ident?.content_allowed === false) {
        console.error("scan moderation blocked", {
          categories: scanData?.ident?.evidence?.moderation?.categories || [],
          ident: scanData?.ident || null,
        });
        setScanError("This item cannot be added because it contains explicit or abusive content.");
      }

      const directScanItemId = directOpenItemId(scanData);
      if (scanData?.auto_open === true && directScanItemId > 0) {
        scanDebugLog("frontend_scan_fast_exact_db_open", {
          item_id: directScanItemId,
          final_source: scanData?.final_source || null,
          match_reason: scanData?.match_reason || scanData?.main_reason || null,
        });
        closeScanModal();
        navigate(buildItemUrl({ ...(scanData?.primary_db_match || {}), item_id: directScanItemId, name: scanData?.primary_db_match?.name || finalQuery }));
        return;
      }

      const scanConfidence = Number(scanData?.confidence ?? scanData?.ident?.confidence ?? 0);
      const validity = getScanValidity(scanData);
      if (scanData?.ident) {
        const scanImageUrl = scanData?.image_url || scanData?.ref_image_url || null;
        const correctedLegoNumber = /^\d{5}$/.test(String(scanData?.lego_number_retry_number || "")) ? String(scanData.lego_number_retry_number) : "";
        const lookupIdent = correctedLegoNumber ? {
          ...scanData.ident,
          number: correctedLegoNumber,
          signals: { ...(scanData.ident?.signals || {}), number: correctedLegoNumber },
        } : scanData.ident;
        const lookupPayload = {
          ident: lookupIdent || null,
          scan_image_url: scanImageUrl,
        };
        scanDebugLog("scan-photo response", {
          ident_present: Boolean(scanData?.ident),
          scanConfidence: Number.isFinite(scanConfidence) ? scanConfidence : null,
          image_url: scanData?.image_url || null,
          ref_image_url: scanData?.ref_image_url || null,
        });
        scanDebugLog("scan-lookup payload", {
          scanConfidence: Number.isFinite(scanConfidence) ? scanConfidence : null,
          image_url: scanData?.image_url || null,
          ref_image_url: scanData?.ref_image_url || null,
          payload: lookupPayload,
        });
        scanDebugLog("scan-lookup request", {
          ident: scanData?.ident || null,
          scan_image_url: scanData?.image_url || scanData?.ref_image_url || null,
        });
        setScanStep("Checking database...");
        const lookupRes = await api.post("/api/scan-lookup", lookupPayload).catch((err) => {
          console.error("scan-lookup error", err?.response?.data || err);
          throw err;
        });
        scanDebugLog("scan-lookup response", lookupRes?.data);
        let result = lookupRes?.data || {};
        if (scanData?.lego_name_candidate_review === true && scanData?.review_only === true && scanData?.primary_db_match && result?.auto_open !== true) {
          result = { ...result, ...scanData, auto_open: false, review_only: true, reason: "lego_name_candidate_number_conflict" };
        }
        const directLookupItemId = directOpenItemId(result);
        const scanHasUncertainFields = Boolean(
          scanData?.has_uncertain_fields ||
          scanData?.ident?.has_uncertain_fields ||
          (Array.isArray(scanData?.uncertain_fields) && scanData.uncertain_fields.length > 0) ||
          (Array.isArray(scanData?.ident?.uncertain_fields) && scanData.ident.uncertain_fields.length > 0)
        );

        // AUTO OPEN EXISTING ITEM
        const shouldOpenExisting =
          result?.auto_open === true &&
          directLookupItemId > 0;
        const hasTrustworthyPrimaryExistingMatch = Boolean(
          !!result?.item_id &&
          result?.existing === true &&
          Number(result?.primary_db_match?.item_id || result?.item_id || 0) === Number(result?.item_id || 0) &&
          (
            Boolean(result?.primary_db_match) ||
            result?.exact_db_match === true ||
            result?.strong_db_match === true ||
            ["near_db_match", "strong_db_match", "exact_db_match"].includes(String(result?.match_tier || ""))
          )
        );
        const shouldOverrideExistingUncertainty = Boolean(
          hasTrustworthyPrimaryExistingMatch &&
          scanHasUncertainFields &&
          result?.auto_open !== true
        );

        if (shouldOpenExisting) {
          scanDebugLog("frontend_scan_existing_auto_open_fired", {
            item_id: directLookupItemId,
            action: result.action || "",
            existing: result.existing,
            match: result.match,
            auto_open: result.auto_open,
            auto_open_reason: result.auto_open_reason || null,
            has_uncertain_fields: scanHasUncertainFields,
          });
          closeScanModal();
          navigate(buildItemUrl({
            ...(result?.primary_db_match || {}),
            ...result,
            item_id: directLookupItemId,
            name: result?.primary_db_match?.name || result?.name || result?.item_name || finalQuery,
          }));
          return;
        }
        if (shouldOverrideExistingUncertainty) {
          scanDebugLog("frontend_scan_existing_auto_open_override_skipped", {
            item_id: result.item_id,
            existing: result.existing,
            has_uncertain_fields: scanHasUncertainFields,
            auto_open: result.auto_open,
            auto_open_allowed: result.auto_open_allowed,
          });
        }
        if (
          !!result?.item_id &&
          result?.existing === true &&
          scanHasUncertainFields &&
          result?.auto_open !== true
        ) {
          scanDebugLog("frontend_scan_existing_auto_open_override_skipped", {
            item_id: result.item_id,
            existing: result.existing,
            has_uncertain_fields: scanHasUncertainFields,
            auto_open: result.auto_open,
            auto_open_allowed: result.auto_open_allowed,
          });
          scanDebugLog("frontend_scan_existing_auto_open_blocked_by_uncertainty", {
            item_id: result.item_id,
            auto_open: result.auto_open,
            auto_open_allowed: result.auto_open_allowed,
            auto_open_reason: result.auto_open_reason || null,
            has_uncertain_fields: scanHasUncertainFields,
          });
        }

        // UNCLEAR SCAN — ask for clearer photo
        if (result.action === "scan_unclear") {
          if (!hasValidScan) {
            console.warn("scan rejected for unclear result", result);
            alert(
              result.message ||
              "We couldn't clearly identify this item. Please upload a clearer photo."
            );
            return;
          }
          console.warn("scan_unclear returned for valid scan, continuing anyway", result);
        }

        // fallback to add-item flow
        if (!result.item_id && result?.existing !== true && result?.match !== true && result?.action !== "scan_unclear") {
          scanDebugLog("scan continuing to add-item flow", result);
        }

        setScanLookupMatch(result);
      }

      if (scanConfidence < 0.8) {
        console.warn("Low confidence scan, continuing anyway:", scanConfidence);
      } else if (!validity.valid) {
        setScanError("This image does not look collectible enough to create directly. Use manual add.");
      }
      if (scanConfidence < 0.8 && !scanData?.ident) {
        setScanLookupMatch({
          match: false,
          primary_db_match: null,
          alternate_db_matches: [],
          creation_candidate: scanData?.ident?.creation_candidate || null,
          market_candidates: scanData?.ident?.market_candidates || [],
        });
      }
      setScanCreateFile({
        blob: uploadBlob,
        name: uploadName || "scan.jpg",
      });

      setScanStep("Ready to add...");
      setScanResult(scanData);
    } catch (err) {
      if (err?.response?.data?.error === "image_rejected") {
        setScanError("That photo cannot be processed. Retake it with only the item in frame.");
        setScanLoading(false);
        setScanStep("");
        setScanResult(null);
        return;
      }
      const detail = getScanCreateErrorMessage(err?.response?.data, err?.message || "Photo scan failed. Please try again.");
      setScanError(safeText(detail));
    } finally {
      setScanLoading(false);
      if (scanInputRef.current) scanInputRef.current.value = "";
    }
  };

  const manualFallbackRaw = (
    buildQueryFromIdent(scanResult) ||
    scanResult?.raw ||
    ""
  ).trim();

  const handleOpenLookupMatch = () => {
    const itemId = primaryDbMatch?.item_id || scanLookupMatch?.item_id;
    if (!itemId) return;
    closeScanModal();
    navigate(buildItemUrl(primaryDbMatch || scanLookupMatch));
  };

  const handleUseScanResult = async () => {
    if (!scanResult?.ident || !scanCreateFile?.blob) {
      setScanError("Missing scan data. Please rescan and try again.");
      return;
    }
    const scanValidity = getScanValidity(scanResult);
    const canonicalDetectedName = cleanScanField(scanEditValue) || getCanonicalScanName(scanResult);
    const editedFallbackAllowed = isUsableEditedName(canonicalDetectedName);
    if ((!scanValidity.valid || scanValidity.confidence < LOW_CONFIDENCE_THRESHOLD || !scanValidity.hasRequiredAnchors) && !editedFallbackAllowed) {
      setScanError("This scan is too weak to create directly. Use manual add instead.");
      return;
    }

    try {
      setScanLoading(true);
      setScanStep("Creating item...");
      const form = new FormData();
      const scanImageValue = scanResult?.image_url || scanResult?.ref_image_url || "";
      form.append("file", scanCreateFile.blob, scanCreateFile.name || "scan.jpg");
      form.append("q", canonicalDetectedName || manualFallbackRaw || "");
      form.append("item_input", canonicalDetectedName || "");
      form.append("edited_name", canonicalDetectedName || "");
      form.append("scan_image_url", scanImageValue);
      form.append("image_url", scanImageValue);
      form.append("ident", JSON.stringify(scanResult.ident));
      let data;
      try {
        const resp = await api.post("/api/scan-create", form);
        data = resp?.data;
        if (data?.error === "image_rejected") {
          setScanError("That photo cannot be processed. Retake it with only the item in frame.");
          setScanLoading(false);
          setScanStep("");
          setScanResult(null);
          return;
        }
      } catch (err) {
        const payload = err?.response?.data;
        if (payload?.error === "image_rejected") {
          setScanError("That photo cannot be processed. Retake it with only the item in frame.");
          setScanLoading(false);
          setScanStep("");
          setScanResult(null);
          return;
        }
        console.error("scan-create failed", {
          status: err?.response?.status || null,
          data: err?.response?.data || null,
          message: err?.message || String(err),
        });
        throw new Error(getScanCreateErrorMessage(payload, err?.message || "Could not process scanned item."));
      }
      const itemId = data?.item_id || data?.data?.item_id || data?.data?.id || null;
      if (!itemId) {
        const payloadSnippet = JSON.stringify(data || {}).slice(0, 240);
        throw new Error(`Missing item id from /api/scan-create response: ${payloadSnippet}`);
      }

      setScanStep("Queuing image...");
      setScanStep("Queuing pricing...");
      setScanStep("Opening item...");

      closeScanModal();
      navigate(buildItemUrl({ item_id: itemId, name: canonicalDetectedName || data?.name || data?.data?.name || manualFallbackRaw }));
    } catch (err) {
      const detail = err?.message || "Could not process scanned item.";
      setScanError(String(detail));
    } finally {
      setScanLoading(false);
      setScanStep("");
    }
  };

  const normalizedPic =
    typeof profilePic === "string" &&
    profilePic.trim() !== "" &&
    profilePic !== "null" &&
    profilePic !== "undefined"
      ? profilePic
      : DEFAULT_AVATAR;

  const scanValidity = scanResult ? getScanValidity(scanResult) : null;
  const canonicalDetectedName = scanEditValue;
  const editedNameUsable = isUsableEditedName(cleanScanField(scanEditValue) || getCanonicalScanName(scanResult));
  const scanCreateUsingEditedFallback = Boolean(
    scanResult?.ok &&
    scanResult?.ident?.content_allowed !== false &&
    editedNameUsable &&
    (
      !scanValidity?.valid ||
      Number(scanValidity?.confidence || 0) < LOW_CONFIDENCE_THRESHOLD ||
      !scanValidity?.hasRequiredAnchors
    )
  );
  const scanCreateDisabled = Boolean(
    scanLoading ||
    !scanResult?.ok ||
    scanResult?.ident?.content_allowed === false ||
    (
      !scanCreateUsingEditedFallback &&
      (
        !scanValidity?.valid ||
        Number(scanValidity?.confidence || 0) < LOW_CONFIDENCE_THRESHOLD ||
        !scanValidity?.hasRequiredAnchors
      )
    )
  );
  const primaryDbMatch = scanLookupMatch?.primary_db_match || null;
  const scanDisplaySummary = scanResult ? getScanDisplaySummary(scanResult) : {};
  const isLegoScanResult = String(scanDisplaySummary?.category || "").trim().toLowerCase() === "lego";
  const detectedLegoNumber = String(scanResult?.lego_number_retry_number || scanDisplaySummary?.number || "").trim();
  const legoExactMatchFound = Boolean(scanLookupMatch?.item_id && scanLookupMatch?.review_only !== true && scanLookupMatch?.reason !== "lego_name_candidate_number_conflict");
  const showLegoNumberCorrection = Boolean(scanResult?.ok && isLegoScanResult && !legoExactMatchFound);
  const alternateDbMatches = Array.isArray(scanLookupMatch?.alternate_db_matches) ? scanLookupMatch.alternate_db_matches : [];
  const sameFamilyDbMatches = Array.isArray(scanLookupMatch?.same_family_db_matches) ? scanLookupMatch.same_family_db_matches : [];
  const existingDbItems = [primaryDbMatch, ...alternateDbMatches, ...sameFamilyDbMatches]
    .filter(Boolean)
    .filter((item, index, items) => items.findIndex((entry) => Number(entry?.item_id || 0) === Number(item?.item_id || 0)) === index);
  const hasSingleExactExistingMatch = (
    existingDbItems.length === 1 &&
    existingDbItems[0]?.base_family_match === true &&
    existingDbItems[0]?.variant_match === true
  );
  const hasMultipleExistingMatches = existingDbItems.length > 1;
  const hasExistingMatchUncertainty = (
    hasMultipleExistingMatches ||
    existingDbItems.some((item) => item?.base_family_match !== true || item?.variant_match !== true)
  );
  const marketReferences = (
    scanLookupMatch?.market_candidates ||
    scanResult?.ident?.market_candidates ||
    []
  ).slice(0, 4);

  const menuItemSx = {
    fontSize: 14,
    fontWeight: 600,
    px: 2,
    py: 1.2,
    borderRadius: 0,
    color: "#0f172a",
    "&:hover": { backgroundColor: "#f1f5f9" },
  };

  const handleManualLegoNumberLookup = async () => {
    if (!/^\d{5}$/.test(legoNumberEdit)) {
      setScanError("LEGO set number must be exactly five digits.");
      return;
    }
    setLegoNumberSaving(true);
    setScanError("");
    try {
      const { data } = await api.post("/api/scan-lego-number-lookup", { number: legoNumberEdit, scan_context: scanResult });
      setScanLookupMatch(data || null);
      setLegoNumberEditing(false);
      if (data?.auto_open === true && Number(data?.item_id || 0) > 0) {
        closeScanModal();
        navigate(buildItemUrl({ ...(data.primary_db_match || {}), item_id: data.item_id }));
      } else if (!data?.match) {
        setScanError("No exact LEGO item matched that set number.");
      }
    } catch (err) {
      setScanError(err?.response?.data?.error === "invalid_lego_number" ? "LEGO set number must be exactly five digits." : "LEGO number lookup failed.");
    } finally { setLegoNumberSaving(false); }
  };

  const menuLinkStyle = { textDecoration: "none", color: "inherit", width: "100%" };
  return (
    <AppBar
      ref={appBarRef}
      position="fixed"
      elevation={0}
      sx={{
        borderRadius: 0,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        backgroundColor: "#0b1220",
        color: "rgba(230,237,247,0.94)",
        width: "100%",
        zIndex: 1300,
      }}
    >
      <Toolbar
        sx={{
          display: isLarge ? "grid" : "flex",
          flexDirection: isMobileLayout ? "column" : "row",
          gridTemplateColumns: isLarge ? "1fr minmax(320px, 560px) 1fr" : "unset",
          minHeight: { xs: 56, md: 64 },
          alignItems: "center",
          justifyContent: isLarge ? "unset" : "flex-start",
          gap: isMobileLayout ? 0.5 : 0.75,
          px: { xs: 1, sm: 1.5, md: 2 },
          py: 0.5,
          borderRadius: 0,
          flexWrap: isMobileLayout ? "wrap" : "nowrap",
          "& .top-nav-link": {
            textDecoration: "none",
            color: "rgba(255,255,255,0.92)",
            transition: "text-decoration-color 160ms ease",
          },
          "& .top-nav-link:hover": {
            textDecoration: "underline",
          },
          "& > *": { minWidth: 0 },
        }}
      >
        {isMobileLayout ? (
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", color: "inherit" }}>
                <Box component="img" src="/logos/LogoNew.png" alt="Collector Stop Logo" sx={{ height: 34, width: "auto" }} />
              </Link>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <IconButton
                onClick={handleMenuOpen}
                aria-label="Open menu"
                sx={{
                  color: "rgba(255,255,255,0.92)",
                  width: 40,
                  height: 40,
                  borderRadius: 0,
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                }}
              >
                <Badge
                  badgeContent={showFriendRequestBadge ? friendRequestBadge : 0}
                  color="error"
                  invisible={!showFriendRequestBadge}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 17,
                      height: 17,
                      px: 0.45,
                      fontSize: 10,
                      fontWeight: 800,
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 22,
                      height: 16,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    {[0, 1, 2].map((line) => (
                      <Box
                        key={line}
                        component="span"
                        sx={{
                          display: "block",
                          width: "100%",
                          height: 2,
                          borderRadius: 1,
                          backgroundColor: "currentColor",
                        }}
                      />
                    ))}
                  </Box>
                </Badge>
              </IconButton>
              <IconButton
                onClick={handleMenuOpen}
                aria-label="Open profile menu"
                sx={{ color: "rgba(255,255,255,0.92)", flexShrink: 0 }}
              >
                <Avatar
                  src={normalizedPic}
                  sx={{ height: 38, width: 38, bgcolor: "#ffffff", border: "1px solid rgba(0,0,0,0.12)" }}
                  imgProps={{
                    referrerPolicy: "no-referrer",
                    onError: (e) => {
                      if (!e.currentTarget.src.endsWith("/logos/default-avatar.jpg")) {
                        e.currentTarget.src = DEFAULT_AVATAR;
                      }
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <>
            {/* LEFT: LOGO */}
            <Box sx={{ display: "flex", alignItems: "center", justifySelf: "start" }}>
              <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", color: "inherit" }}>
                <Box
                  component="img"
                  src="/logos/LogoNew.png"
                  alt="Collector Stop Logo"
                  sx={{ height: { xs: 34, md: 44 }, width: "auto" }}
                />
              </Link>
            </Box>
          </>
        )}

        {/* SEARCH */}
        <Box
          sx={{
            gridColumn: isLarge ? "2" : "auto",
            justifySelf: isLarge ? "center" : "unset",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: isLarge ? "unset" : 1,
            mx: isLarge ? 0 : 0,
            minWidth: 0,
            width: isLarge ? "100%" : "100%",
            maxWidth: isLarge ? "560px" : "100%",
            mt: isMobileLayout ? 0.5 : 0,
          }}
        >
          <TextField
            placeholder="Search..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              handleSearch();
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                height: isLarge ? 38 : 36,
                bgcolor: "rgba(255,255,255,0.10)",
                borderRadius: 0,
                border: "1px solid rgba(255,255,255,0.14)",
                transition: "border-color 160ms ease, box-shadow 160ms ease",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.24)",
                },
                "&.Mui-focused": {
                  borderColor: "rgba(60,110,113,0.85)",
                  boxShadow: "0 0 0 3px rgba(60,110,113,0.28)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
              "& .MuiInputBase-input": {
                color: "#fff",
                py: isUnder425 ? 0.55 : 0.7,
                "&::placeholder": {
                  color: "rgba(255,255,255,0.70)",
                  opacity: 1,
                },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            size={isUnder425 ? "small" : "medium"}
            sx={{
              height: isUnder425 ? 34 : isLarge ? 38 : 36,
              minWidth: isUnder425 ? 52 : 78,
              borderRadius: 0,
              px: isUnder425 ? 1.2 : 1.8,
              fontSize: isUnder425 ? 12 : 14,
              lineHeight: 1.2,
              flexShrink: 0,
              whiteSpace: "nowrap",
              boxShadow: "0 4px 12px rgba(0,0,0,0.28)",
            }}
          >
            {isUnder425 ? "Go" : "Search"}
          </Button>
          <Button
            variant="contained"
            onClick={openScanModal}
            size={isUnder425 ? "small" : "medium"}
            sx={{
              height: isUnder425 ? 34 : isLarge ? 38 : 36,
              minWidth: isUnder425 ? 62 : 84,
              borderRadius: 0,
              px: isUnder425 ? 1.2 : 1.8,
              fontSize: isUnder425 ? 12 : 14,
              lineHeight: 1.2,
              flexShrink: 0,
              whiteSpace: "nowrap",
              boxShadow: "0 4px 12px rgba(0,0,0,0.28)",
            }}
          >
            Scan
          </Button>
        </Box>

        {/* RIGHT: LINKS (desktop only) + AVATAR */}
        {isLarge && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
              justifySelf: "end",
            }}
          >
            <Link to="/community" className="top-nav-link">
              Community
            </Link>
            <Link to="/mycollection" className="top-nav-link">
              My Collection
            </Link>
            {showPrices && <Link to="/profitloss" className="top-nav-link">
              Profit &amp; Loss
            </Link>}
            <IconButton
              onClick={handleMenuOpen}
              aria-label="Open menu"
              sx={{
                color: "rgba(255,255,255,0.92)",
                width: 40,
                height: 40,
                borderRadius: 0,
                flexShrink: 0,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <Badge
                badgeContent={showFriendRequestBadge ? friendRequestBadge : 0}
                color="error"
                invisible={!showFriendRequestBadge}
                sx={{
                  "& .MuiBadge-badge": {
                    minWidth: 17,
                    height: 17,
                    px: 0.45,
                    fontSize: 10,
                    fontWeight: 800,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 22,
                    height: 16,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {[0, 1, 2].map((line) => (
                    <Box
                      key={line}
                      component="span"
                      sx={{
                        display: "block",
                        width: "100%",
                        height: 2,
                        borderRadius: 1,
                        backgroundColor: "currentColor",
                      }}
                    />
                  ))}
                </Box>
              </Badge>
            </IconButton>
            <IconButton
              onClick={handleMenuOpen}
              aria-label="Open profile menu"
              sx={{ color: "rgba(255,255,255,0.92)" }}
            >
              <Avatar
                src={normalizedPic}
                sx={{ height: 40, width: 40, bgcolor: "#ffffff", border: "1px solid rgba(0,0,0,0.12)" }}
                imgProps={{
                  referrerPolicy: "no-referrer",
                  onError: (e) => {
                    if (!e.currentTarget.src.endsWith("/logos/default-avatar.jpg")) {
                      e.currentTarget.src = DEFAULT_AVATAR;
                    }
                  },
                }}
              />
            </IconButton>
          </Box>
        )}

        {/* MENU */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          disableScrollLock
          PaperProps={{
            sx: {
              borderRadius: 4,
              mt: 1,
              minWidth: 220,
              backgroundColor: "background.paper",
              color: "#0f172a",
              border: "1px solid rgba(15,23,42,0.15)",
              boxShadow: "0 12px 30px rgba(15,23,42,0.18)",
              overflow: "hidden",
            },
          }}
        >
          {!isLoggedIn ? (
            <>
              {!isLarge && (
                <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                  <Link to="/community" style={menuLinkStyle}>
                    Community
                  </Link>
                </MenuItem>
              )}
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to="/accounts" style={menuLinkStyle}>
                  Collectors
                </Link>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to="/manage" style={menuLinkStyle}>
                  Manage
                </Link>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to={wishlistPath} style={menuLinkStyle}>
                  Wishlist
                </Link>
              </MenuItem>
              {!isLarge && (
                <>
                  <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                    <Link to="/mycollection" style={menuLinkStyle}>
                      My Collection
                    </Link>
                  </MenuItem>
                  {showPrices && <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                    <Link to="/profitloss" style={menuLinkStyle}>
                      Profit &amp; Loss
                    </Link>
                  </MenuItem>}
                </>
              )}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to="/login" style={menuLinkStyle}>
                  Login
                </Link>
              </MenuItem>
            </>
          ) : (
            <>
              {!isLarge && (
                <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                  <Link to="/community" style={menuLinkStyle}>
                    Community
                  </Link>
                </MenuItem>
              )}
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to="/accounts" style={menuLinkStyle}>
                  Collectors
                </Link>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to="/manage" style={menuLinkStyle}>
                  Manage
                </Link>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                <Link to={wishlistPath} style={menuLinkStyle}>
                  Wishlist
                </Link>
              </MenuItem>
              {!isLarge && (
                <>
                  <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                    <Link to="/mycollection" style={menuLinkStyle}>
                      My Collection
                    </Link>
                  </MenuItem>
                  {showPrices && <MenuItem onClick={handleMenuClose} sx={menuItemSx}>
                    <Link to="/profitloss" style={menuLinkStyle}>
                      Profit &amp; Loss
                    </Link>
                  </MenuItem>}
                </>
              )}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  ...menuItemSx,
                  color: "#dc2626",
                  fontWeight: 700,
                  "&:hover": { backgroundColor: "#fef2f2" },
                }}
              >
                Logout
              </MenuItem>
            </>
          )}
        </Menu>
      </Toolbar>
      <TickerBar />
      <Dialog open={scanOpen} onClose={closeScanModal} fullWidth maxWidth="sm">
        <DialogTitle>Scan Item</DialogTitle>
        <DialogContent dividers>
          <input
            ref={scanInputRef}
            type="file"
            accept="image/*"
            onChange={handleScanFileChange}
            style={{ display: "none" }}
          />

          {!scanResult && !scanLoading && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Take a photo or choose an image to scan.
              </Typography>
              <Button variant="contained" onClick={handleChooseScanFile}>
                Take/Upload Photo
              </Button>
            </Box>
          )}

          {scanLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={22} />
              <Typography>{scanStep || "Scanning..."}</Typography>
            </Box>
          )}

          {scanResult?.ok && !scanLoading && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {(() => {
                const summary = getScanDisplaySummary(scanResult);
                const detailChips = [
                  summary.category,
                  summary.brand,
                  summary.setName,
                  summary.number ? `#${String(summary.number).replace(/^#\s*/, "")}` : "",
                  summary.variant,
                ].filter(Boolean);
                return (
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle2">Detected</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {canonicalDetectedName || "Scanned item"}
                  </Typography>
                  {summary.name && summary.name !== canonicalDetectedName && (
                    <Typography variant="body2" color="text.secondary">
                      Name: {summary.name}
                    </Typography>
                  )}
                {detailChips.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {detailChips.join(" • ")}
                  </Typography>
                )}
              </Box>
                );
              })()}
              {(Number(scanValidity?.confidence || 0) < LOW_CONFIDENCE_THRESHOLD) && (
                <Typography variant="body2" color="warning.main">
                  Low confidence scan. Review the detected name before creating the item.
                </Typography>
              )}
              {scanResult?.ident?.content_allowed === false && (
                <Typography variant="body2" color="error.main">
                  This item cannot be added because it contains explicit or abusive content.
                </Typography>
              )}
              {scanValidity && !scanValidity.valid && (
                <Typography variant="body2" color="warning.main">
                  This image does not have enough collectible-specific detail to create directly.
                </Typography>
              )}
              {scanCreateUsingEditedFallback && (
                <Typography variant="body2" color="text.secondary">
                  Using edited name to create item.
                </Typography>
              )}
              {scanLookupMatch?.base_family_match && scanLookupMatch?.variant_match === false && (
                <Typography variant="body2" color="warning.main">
                  We found similar items, but the detected variant may differ.
                </Typography>
              )}
              {hasExistingMatchUncertainty && existingDbItems.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  We found similar items. Choose the correct variant below.
                </Typography>
              )}
              {showLegoNumberCorrection && (
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={700}>Detected LEGO set number: {detectedLegoNumber || "Unknown"}</Typography>
                  {!legoNumberEditing ? (
                    <Button sx={{ mt: 1, minHeight: 44 }} variant="outlined" onClick={() => { setLegoNumberEdit(/^\d{5}$/.test(detectedLegoNumber) ? detectedLegoNumber : ""); setLegoNumberEditing(true); }}>Edit number</Button>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mt: 1 }}>
                      <TextField fullWidth label="Five-digit LEGO set number" inputProps={{ inputMode: "numeric", pattern: "[0-9]{5}", maxLength: 5 }} value={legoNumberEdit} onChange={(e) => setLegoNumberEdit(e.target.value.replace(/\D/g, "").slice(0, 5))} />
                      <Button variant="contained" disabled={legoNumberSaving || !/^\d{5}$/.test(legoNumberEdit)} onClick={handleManualLegoNumberLookup} sx={{ minHeight: 48 }}>{legoNumberSaving ? "Checking…" : "Check number"}</Button>
                    </Box>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing items in your database
                  </Typography>
                  {existingDbItems.length > 0 ? existingDbItems.map((item) => (
                    <Box
                      key={item.item_id}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 1, gap: 2 }}
                    >
                      <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 1, overflow: "hidden", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {item.image_url ? (
                          <Box
                            component={ImageWithFallback}
                            src={item.image_url}
                            fallbackSrcs={[item.ref_image_url]}
                            placeholderSrc="/logos/default-item.jpg"
                            alt={item.name || "Database item"}
                            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <Box sx={{ width: "100%", height: "100%", bgcolor: "#f8fafc" }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>
                          {item.name || `Item #${item.item_id}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {[item.category, item.brand, item.set_name, item.number ? `#${item.number}` : ""].filter(Boolean).join(" | ")}
                        </Typography>
                        {item.match_tier === "same_family_db_match" && (
                          <Typography variant="caption" color="text.secondary">
                            Same item family in database
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                          closeScanModal();
                          navigate(buildItemUrl(item));
                        }}
                      >
                        Open Existing Item
                      </Button>
                    </Box>
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No strong database match was found.
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Reference listings
                  </Typography>
                  {marketReferences.length > 0 ? marketReferences.map((item, idx) => (
                    <Box
                      key={item.detail_url || item.label || idx}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 1, gap: 2, opacity: 0.9 }}
                    >
                      <Box sx={{ width: 52, height: 52, flexShrink: 0 }}>
                        <Box
                          component={ImageWithFallback}
                          src={item.image_url}
                          fallbackSrcs={[item.ref_image_url]}
                          placeholderSrc="/default-avatar.jpg"
                          alt={item.label || "Reference listing"}
                          sx={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 2 }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>
                          {item.label || "Reference listing"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {[item.set_name, item.number ? `#${item.number}` : "", item.source].filter(Boolean).join(" | ")}
                        </Typography>
                      </Box>
                      <Button variant="outlined" onClick={() => setScanEditValue(cleanScanField(item.label || ""))}>
                        Use Title
                      </Button>
                    </Box>
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No clean reference listings are needed for this scan.
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Edit detected name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={scanEditValue}
                  onChange={(e) => setScanEditValue(e.target.value)}
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: 1,
                    mb: 1,
                  }}
                />
              </Box>
            </Box>
          )}

          {scanError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {safeText(scanError)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button onClick={closeScanModal} disabled={scanLoading}>Cancel</Button>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {scanResult?.ok && (
                <Button
                  onClick={() => {
                    const q = canonicalDetectedName.trim();
                    if (!q) return;
                    closeScanModal();
                    navigate(`/add?query=${encodeURIComponent(q)}`);
                }}
                variant="outlined"
                disabled={scanLoading || scanResult?.ident?.content_allowed === false}
              >
                Add Item Manually
              </Button>
            )}
            {hasSingleExactExistingMatch && primaryDbMatch?.item_id ? (
              <Button onClick={handleOpenLookupMatch} variant="contained" disabled={scanLoading}>
                Open Existing Item
              </Button>
            ) : scanResult?.ok ? (
              <Button onClick={handleUseScanResult} variant="contained" disabled={scanCreateDisabled}>
                Create New Item
              </Button>
            ) : null}
          </Box>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Header;
